import { useEffect, useRef } from 'react';

const facts = [
  {
    icon: 'ri-earth-line',
    number: '91%',
    label: 'del plástico nunca se recicla',
    description: 'Solo el 9% del plástico producido en la historia ha sido reciclado. El resto termina en ríos, suelos y océanos por hasta 500 años.',
    color: '#c62828',
  },
  {
    icon: 'ri-tree-line',
    number: '15B',
    label: 'árboles talados al año',
    description: 'Cada minuto desaparecen 30 campos de fútbol de bosque. Reciclar papel protege los ecosistemas que Santander necesita para sobrevivir.',
    color: '#2e7d32',
  },
  {
    icon: 'ri-water-flash-line',
    number: '70%',
    label: 'del agua dulce en riesgo',
    description: 'La contaminación por residuos destruye fuentes hídricas. El río Chicamocha y otros ríos de Santander sufren sus consecuencias hoy.',
    color: '#0277bd',
  },
  {
    icon: 'ri-recycle-line',
    number: '×5',
    label: 'menos energía al reciclar aluminio',
    description: 'Reciclar una sola lata ahorra el 95% de la energía para fabricarla nueva. Multiplicado por millones, el impacto es enorme.',
    color: '#e65100',
  },
];

const whyItems = [
  {
    title: 'Reduce la contaminación de raíz',
    body: 'Los residuos mal gestionados liberan gases de efecto invernadero y toxinas que envenenan el aire, el agua y el suelo durante décadas.',
    icon: 'ri-cloud-windy-line',
  },
  {
    title: 'Conserva lo que queda',
    body: 'Cada tonelada de papel reciclado salva 17 árboles y 26.000 litros de agua. Los recursos del planeta son finitos — el tiempo también.',
    icon: 'ri-leaf-line',
  },
  {
    title: 'Activa la economía circular',
    body: 'El reciclaje crea empleos dignos, reduce costos y construye comunidades resilientes. En Santander, cada punto activo fortalece la economía local.',
    icon: 'ri-loop-right-line',
  },
  {
    title: 'Protege la biodiversidad única',
    body: 'La contaminación destruye hábitats únicos. Reciclar reduce la presión sobre los ecosistemas que hacen de Santander un lugar extraordinario.',
    icon: 'ri-seedling-line',
  },
];

export default function RecyclingImportanceSection() {
  const factsRef = useRef<HTMLDivElement>(null);
  const whyRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const splitRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          if (entry.target === headerRef.current) {
            const children = Array.from(entry.target.children) as HTMLElement[];
            children.forEach((child, i) => {
              setTimeout(() => {
                child.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
                child.style.opacity = '1';
                child.style.transform = 'none';
              }, i * 150);
            });
          }

          if (entry.target === factsRef.current) {
            const cards = entry.target.querySelectorAll('.fact-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = '1';
                (card as HTMLElement).style.transform = 'translateY(0) scale(1)';
              }, i * 120);
            });
          }

          if (entry.target === whyRef.current) {
            const items = entry.target.querySelectorAll('.why-item');
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = '1';
                (item as HTMLElement).style.transform = 'translateX(0)';
              }, i * 130);
            });
          }

          if (entry.target === splitRef.current) {
            const children2 = Array.from(entry.target.children) as HTMLElement[];
            children2.forEach((child, i) => {
              setTimeout(() => {
                child.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
                child.style.opacity = '1';
                child.style.transform = 'none';
              }, i * 200);
            });
          }

          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12 }
    );

    [headerRef, factsRef, whyRef, splitRef].forEach((r) => {
      if (r.current) observer.observe(r.current);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-[#fafaf8] py-28 md:py-36 px-6 md:px-14 overflow-hidden">

      {/* ── HEADER ── */}
      <div ref={headerRef} className="max-w-6xl mx-auto mb-20">
        <div style={{ opacity: 0, transform: 'translateY(20px)' }}>
          <span
            className="inline-block text-[#43a047] text-[10px] tracking-[0.45em] uppercase font-semibold mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Por qué actuar ahora
          </span>
        </div>
        <div style={{ opacity: 0, transform: 'translateY(20px)' }}>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <h2
              className="text-[#1b5e20] font-bold leading-tight"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(2.2rem, 4.5vw, 4rem)',
              }}
            >
              El reciclaje no es<br />
              una opción —<br />
              <em className="not-italic" style={{ color: '#c62828' }}>es urgente.</em>
            </h2>
            <p
              className="text-[#4a5568] text-base leading-relaxed max-w-md"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Los datos son contundentes y las imágenes no mienten. En los 87 municipios de Santander, 
              la Fundación de Reciclaje en Acción Inmediata de Santander trabaja todos los días para revertir 
              el daño — pero necesitamos más manos.
            </p>
          </div>
        </div>
      </div>

      {/* ── SPLIT SECTION: Photo + Quote ── */}
      <div ref={splitRef} className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
        {/* Real photo — emotional */}
        <div
          className="relative rounded-2xl overflow-hidden"
          style={{ minHeight: '380px', opacity: 0, transform: 'translateX(-40px)' }}
        >
          <img
            src="https://storage.readdy-site.link/project_files/99b3bf7a-f106-44a8-ac25-700226859767/5db277f2-f0e0-44b3-8da0-2a5648c0c8b1_WhatsApp-Image-2026-04-19-at-12.04.00.jpeg?v=03d797d887b63d9d51b5d0d5df8ed91d"
            alt="Llantas y basura abandonadas en espacio público de Santander"
            className="w-full h-full object-cover object-center"
            style={{ minHeight: '380px' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <span
              className="inline-flex items-center gap-1.5 bg-[#c62828]/90 text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full mb-3"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              <i className="ri-alert-fill"></i>Santander, 2024
            </span>
            <p className="text-white/70 text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Fotografía real de nuestro equipo. Esta zona fue intervenida por Fundación Frais.
            </p>
          </div>
        </div>

        {/* Facts stacked */}
        <div
          className="flex flex-col gap-5"
          style={{ opacity: 0, transform: 'translateX(40px)' }}
        >
          {facts.map((fact) => (
            <div
              key={fact.number}
              className="flex items-start gap-5 bg-white rounded-2xl p-6 hover:-translate-y-0.5 transition-all duration-200"
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: fact.color + '15' }}
              >
                <i className={`${fact.icon} text-xl`} style={{ color: fact.color }}></i>
              </div>
              <div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span
                    className="text-2xl font-bold"
                    style={{ color: fact.color, fontFamily: "'Playfair Display', serif" }}
                  >
                    {fact.number}
                  </span>
                  <span
                    className="text-[#374151] text-xs font-bold uppercase tracking-wider"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {fact.label}
                  </span>
                </div>
                <p
                  className="text-[#6b7280] text-xs leading-relaxed"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {fact.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── WHY RECYCLE ── */}
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-10">
          <div className="w-8 h-px bg-[#43a047]"></div>
          <span
            className="text-[#43a047] text-xs tracking-[0.4em] uppercase"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Razones para actuar hoy
          </span>
        </div>

        <div ref={whyRef} className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {whyItems.map((item, i) => (
            <div
              key={item.title}
              className="why-item flex gap-5 bg-white rounded-2xl p-7 hover:bg-[#f1f8e9] transition-colors duration-300"
              style={{
                opacity: 0,
                transform: i % 2 === 0 ? 'translateX(-40px)' : 'translateX(40px)',
                transition: 'opacity 0.8s ease, transform 0.8s ease',
              }}
            >
              <div className="flex-shrink-0 pt-1 w-10 h-10 flex items-center justify-center rounded-full bg-[#e8f5e9]">
                <i className={`${item.icon} text-[#2e7d32] text-base`}></i>
              </div>
              <div>
                <h3
                  className="text-[#1b5e20] text-base font-bold mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {item.title}
                </h3>
                <p
                  className="text-[#6b7280] text-sm leading-relaxed"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {item.body}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Quote */}
        <div className="mt-16 border-t border-[#e5e7eb] pt-14 text-center">
          <blockquote
            className="text-[#1b5e20] text-xl md:text-2xl font-light leading-relaxed max-w-3xl mx-auto"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            &ldquo;No heredamos la tierra de nuestros ancestros,{' '}
            <em className="not-italic font-semibold" style={{ color: '#c62828' }}>
              la tomamos prestada de nuestros hijos.
            </em>&rdquo;
          </blockquote>
          <p
            className="text-[#9ca3af] text-xs tracking-widest uppercase mt-5"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            — Antoine de Saint-Exupéry
          </p>
        </div>
      </div>
    </section>
  );
}
